/**
 * The entries of the in-house resource center (#25353): what each one is called and what it does.
 * Every entry hands off to a widget we already own, through the window events those widgets listen
 * for, so this module has no dependency on HubSpot or GitBook.
 */

export type ResourceCenterEntryId = 'help' | 'ask' | 'feedback' | 'chat' | 'announcement'

export interface ResourceCenterEntry {
  id: Exclude<ResourceCenterEntryId, 'announcement'>
  label: string
  description: string
}

/**
 * The "Give Feedback" HubSpot form, opened with the user's email prefilled (as `/hs-forms/:formId`
 * does). The id is the one the Userflow resource center's "Give Feedback" action linked to. If it is
 * empty, the entry is left out rather than shown broken.
 */
export const FEEDBACK_FORM_ID = '2lWvG7wcPSoCUqxh6VbYCZQ'
const HUBSPOT_FORMS_BASE_URL = 'https://5nj1jy.share-na3.hsforms.com'

export const OPEN_DOCS_EVENT = 'ps:open-docs'
export const OPEN_CHAT_EVENT = 'ps:open-hubspot-chat'

export interface EntryOptions {
  /** HubSpot chat is mounted (the contractor app only). */
  chat: boolean
  /** Override of `FEEDBACK_FORM_ID`, for tests. */
  feedbackFormId?: string
}

export function getFeedbackUrl(email: string | undefined, formId: string = FEEDBACK_FORM_ID): string | null {
  if (!formId) return null
  const query = email ? `?email=${encodeURIComponent(email)}` : ''
  return `${HUBSPOT_FORMS_BASE_URL}/${encodeURIComponent(formId)}${query}`
}

/** The link entries, in panel order. Announcement history is a separate slot, not a link. */
export function getResourceCenterEntries({
  chat,
  feedbackFormId = FEEDBACK_FORM_ID
}: EntryOptions): ResourceCenterEntry[] {
  const entries: ResourceCenterEntry[] = [
    { id: 'help', label: 'Help Articles', description: 'Browse guides on help.paintscout.com' },
    { id: 'ask', label: 'Ask PaintScout', description: 'Get an answer from our AI assistant' }
  ]
  if (feedbackFormId) {
    entries.push({ id: 'feedback', label: 'Give Feedback', description: 'Tell us what to improve' })
  }
  if (chat) {
    entries.push({ id: 'chat', label: 'Live Chat', description: 'Talk to our support team' })
  }
  return entries
}

/**
 * Opens an entry. The caller closes the panel first, so the widget that opens has the corner.
 * Returns false when the entry could not open (no feedback form configured).
 */
export function runResourceCenterEntry(
  id: ResourceCenterEntry['id'],
  { email, feedbackFormId = FEEDBACK_FORM_ID }: { email?: string; feedbackFormId?: string } = {}
): boolean {
  switch (id) {
    case 'help':
      window.dispatchEvent(new Event(OPEN_DOCS_EVENT))
      return true
    case 'ask':
      window.dispatchEvent(new CustomEvent(OPEN_DOCS_EVENT, { detail: { assistant: true } }))
      return true
    case 'chat':
      window.dispatchEvent(new Event(OPEN_CHAT_EVENT))
      return true
    case 'feedback': {
      const url = getFeedbackUrl(email, feedbackFormId)
      if (!url) return false
      window.open(url, '_blank', 'noopener,noreferrer')
      return true
    }
    default:
      return false
  }
}
